import store from '@/state/store'
import { Helper } from '@/mixin/helper'

import * as Customer from '@/services/rest-api/lib/customer/customer'
import * as MarketingUser from '@/services/rest-api/lib/customer/marketing-user'
import * as Vendor from '@/services/rest-api/lib/vendor/vendor'
import VendorStatus from '@/router/views/delivery/components/VendorStatus'
import ReadOnly from '@/router/views/delivery/components/ReadOnly'
import { GET_USER_GROUP } from '@/services/api/setting/user/userGroup'
import { TMS_MARKETING_USER_FILTER } from '@/helpers/tms-access'

const getFeatureFlagValue = store.getters['identity/getFeatureFlagValue']
const hasAccessAll = store.getters['identity/hasAccessAll']
const hasTmsUserGroupAccess = hasAccessAll(['tms-user-group:read']) && Helper.methods.isSuperUser()
const hasBulkDeliveryNote = getFeatureFlagValue('bulkDeliveryNoteFlag')

const translate = (t, key, fallback, params = {}) => {
  if (typeof t !== 'function') return fallback

  const translated = t(key, params)

  return translated === key ? fallback : translated
}

const selectField = (t, key, fallback) =>
  `${translate(t, 'global.form.select', 'Pilih')} ${translate(t, key, fallback)}`

const enterField = (t, key, fallback) =>
  `${translate(t, 'global.form.enter', 'Masukkan')} ${translate(t, key, fallback)}`

export const getBasicInfoFields = (t) => [
  {
    name: 'customer_id',
    isShown: true,
    showKey: 'shipped_customer',
    type: 'value-help-v2',
    label: translate(t, 'delivery.fields.shippedCustomerName', 'Pelanggan Penerima'),
    placeholder: translate(t, 'delivery.placeholder.shippedCustomer', 'Pilih Pelanggan Penerima'),
    validation: 'required',
    trackBy: 'id',
    displayedBy: 'name',
    listGetter: Customer.get,
    hasStoreAction: true,
    apiClient: {
      store: Customer.store,
      show: Customer.show,
    },
    tableFields: [
      {
        label: translate(t, 'delivery.fields.customerName', 'Nama Pelanggan'),
        name: 'name',
        key: 'name',
        isSortable: true,
        isRedirectable: true,
        isEditable: true,
        validation: 'required',
        placeholder: enterField(t, 'delivery.fields.customerName', 'Nama Pelanggan'),
        thStyle: 'width: 350px',
      },
      {
        label: translate(t, 'delivery.fields.marketingEmployee', 'Karyawan Pemasaran'),
        key: 'marketing_user.name',
        isSortable: true,
        isRedirectable: true,
        isEditable: true,
        type: 'select-resourceful-v2',
        name: 'marketing_user_id',
        optionsGetter: MarketingUser.get(TMS_MARKETING_USER_FILTER),
        optionLabelFn: ({ item }) => item.name,
        placeholder: selectField(t, 'delivery.fields.marketingEmployee', 'Karyawan Pemasaran'),
      },
    ],
  },
  {
    name: 'billed_customer_id',
    isShown: true,
    showKey: 'billed_customer',
    type: 'value-help-v2',
    label: translate(t, 'delivery.fields.billedCustomerName', 'Pelanggan Tertagih'),
    placeholder: selectField(t, 'delivery.fields.billedCustomerName', 'Pelanggan Tertagih'),
    trackBy: 'id',
    displayedBy: 'name',
    listGetter: Customer.get,
    hasStoreAction: true,
    apiClient: {
      store: Customer.store,
      show: Customer.show,
    },
    tableFields: [
      {
        label: translate(t, 'delivery.fields.customerName', 'Nama Pelanggan'),
        name: 'name',
        key: 'name',
        isSortable: true,
        isRedirectable: true,
        isEditable: true,
        validation: 'required',
        placeholder: enterField(t, 'delivery.fields.customerName', 'Nama Pelanggan'),
        thStyle: 'width: 350px',
      },
      {
        label: translate(t, 'delivery.fields.marketingEmployee', 'Karyawan Pemasaran'),
        key: 'marketing_user.name',
        isSortable: true,
        isRedirectable: true,
        isEditable: true,
        type: 'select-resourceful-v2',
        name: 'marketing_user_id',
        optionsGetter: MarketingUser.get(TMS_MARKETING_USER_FILTER),
        optionLabelFn: ({ item }) => item.name,
        placeholder: selectField(t, 'delivery.fields.marketingEmployee', 'Karyawan Pemasaran'),
      },
    ],
    disabled: true,
  },
  {
    name: 'marketing_user_id',
    isShown: true,
    showKey: 'marketing_user',
    type: 'select-resourceful-v2',
    label: translate(t, 'delivery.fields.marketingEmployee', 'Karyawan Pemasaran'),
    optionsGetter: MarketingUser.get(TMS_MARKETING_USER_FILTER),
    optionLabelFn: ({ item }) => item.name,
    placeholder: selectField(t, 'delivery.fields.marketingEmployee', 'Karyawan Pemasaran'),
  },
  {
    name: 'vendor_id',
    isShown: hasAccessAll(['vms:create', 'vms:read', 'vms:update', 'vms:delete']),
    showKey: 'vendor',
    type: 'vendor-value-help',
    label: translate(t, 'delivery.fields.vendorName', 'Nama Vendor'),
    placeholder: selectField(t, 'delivery.fields.vendorName', 'Nama Vendor'),
    trackBy: 'id',
    displayedBy: 'name',
    listGetter: Vendor.getRatesWithFilters(['status.name eq ACTIVE', 'paired_company_id gt 0']),
    headerInfoComponent: ReadOnly,
    headerInfoProps: {
      items: [
        {
          key: 'box_type',
          label: translate(t, 'global.field.vehicleType', 'Jenis Kendaraan'),
          bold: true,
          value: '',
        },
        {
          key: 'origin',
          label: translate(t, 'delivery.fields.originCity', 'Kota Asal'),
          bold: true,
          value: '',
        },
        {
          key: 'destination',
          label: translate(t, 'delivery.fields.destinationCity', 'Kota Tujuan'),
          bold: true,
          value: '',
        },
        {
          key: 'total_weight',
          label: translate(t, 'delivery.vendorField.weightTotalKg', 'Total Berat (Kg)'),
          bold: true,
          value: '',
        },
      ],
    },
    tableFields: [
      {
        label: translate(t, 'delivery.fields.vendorName', 'Nama Vendor'),
        key: 'name',
      },
      {
        label: translate(t, 'delivery.vendorField.connected', 'Terhubung'),
        key: 'status.name',
        name: 'status',
        isSlot: true,
        slotComponent: VendorStatus,
        thStyle: 'text-align:center;',
        tdAttr: {
          style: 'text-align:center;',
        },
      },
      {
        label: translate(t, 'delivery.vendorField.contractNumber', 'Nomor Kontrak'),
        key: 'contract_number',
      },
      {
        label: translate(t, 'delivery.vendorField.rate', 'Tarif'),
        key: 'base_price',
        formatter: (value) => (value ? value.toLocaleString() : value),
      },
      {
        label: translate(t, 'delivery.vendorField.origin', 'Asal'),
        key: 'origin',
      },
      {
        label: translate(t, 'delivery.vendorField.destination', 'Tujuan'),
        key: 'destination',
      },
      {
        label: translate(t, 'delivery.vendorField.boxType', 'Jenis Box'),
        key: 'box_type',
      },
      {
        label: translate(t, 'delivery.vendorField.weightCapacity', 'Kapasitas Berat (Kg)'),
        key: 'total_weight',
        formatter: (value) => (value ? value.toLocaleString() : value),
      },
      {
        label: translate(t, 'delivery.vendorField.volumeCapacity', 'Kapasitas Volume (Cbm)'),
        key: 'total_volume',
        formatter: (value) => (value ? value.toLocaleString() : value),
      },
    ],
  },
  {
    name: 'user_group_id',
    isShown: hasTmsUserGroupAccess,
    label: translate(t, 'delivery.fields.userGroup', 'Grup Pengguna'),
    type: 'value-help-v2',
    placeholder: selectField(t, 'delivery.fields.userGroup', 'Grup Pengguna'),
    trackBy: 'id',
    displayedBy: 'name',
    validation: 'required',
    listGetter: async function (opts) {
      let result = {}
      const filter = {
        pagination: {
          page: opts?.page,
          show: opts?.limit,
        },
        search: opts?.search,
      }

      const res = await this.$apollo.query({
        query: GET_USER_GROUP,
        fetchPolicy: 'no-cache',
        variables: {
          filter: {
            ...filter,
          },
        },
      })

      const ugroups = res.data.ugroups.ugroups

      result = {
        metadata: {
          total_count: ugroups.pagination.total,
          page: ugroups.pagination.page,
        },
        data: {
          paginated_result: ugroups.data,
        },
      }

      return result
    },
    multiple: false,
    tableFields: [
      {
        label: translate(t, 'delivery.fields.userGroup', 'Grup Pengguna'),
        key: 'name',
        isSortable: false,
      },
      {
        label: translate(t, 'delivery.fields.vehicleGroup', 'Grup Kendaraan'),
        key: 'vgroups',
        isSortable: false,
        formatter: (value) => {
          return value?.length != null
            ? value.length === 0
              ? translate(t, 'delivery.fields.allVehicleGroup', 'Semua Grup Kendaraan')
              : value.length
            : '-'
        },
      },
    ],
  },
  {
    name: 'note',
    isShown: true,
    showKey: 'note',
    type: 'textarea',
    label: translate(t, 'delivery.noteField.other', 'Catatan Lainnya'),
    placeholder: translate(t, 'delivery.noteField.enterOther', 'Masukkan catatan lainnya disini...'),
    rows: 4,
    validation: 'max:255,length',
  },
]
